import * as React from "react";
import { useTranslation } from "react-i18next";
import { MoreHorizontal } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SHEET_CLOSE_CLASS } from "@/components/ui/sheetChrome";
import { cn } from "@/lib/utils";

/**
 * EL «⋯» DE LA CABECERA, UNO SOLO PARA TODA LA APP (26-09-2026).
 *
 * Hasta hoy cada hoja que necesitaba acciones secundarias se hacía el suyo:
 * `FocusSheet` con `MoreHorizontal` sobre el círculo de la X, Inventario y Equipo
 * con `MoreVertical` y otros tamaños. La ficha `patrones-ui` lo tenía apuntado
 * como trampa («No hay componente compartido para «⋯»: copia el de FocusSheet»).
 * Esto ES esa copia, puesta en un sitio donde se reutiliza en vez de copiarse.
 *
 * - El botón es el mismo círculo gris de la X (`SHEET_CLOSE_CLASS`): los dos
 *   miden y se alinean igual, y si un día cambia el cromo cambian juntos (U1).
 * - Va a la IZQUIERDA de la X, en su misma fila: lo que cierra se queda en la
 *   esquina, donde el pulgar lo busca (U2).
 * - Abrirlo NO pasa por el cierre de la hoja: es un menú, no un «tocar fuera»,
 *   así que con cambios sin guardar no salta la confirmación de descarte (U7).
 * - El contenido va en `z-[60]` como en FocusSheet: por encima de la hoja (z-50),
 *   que lo monta en su propio portal.
 *
 * Los hijos son `SheetMoreMenuItem` (el `DropdownMenuItem` de siempre). Una hoja
 * que no pasa nada no pinta el botón: la cabecera se queda como estaba.
 */
export function SheetMoreMenu({
  children,
  label,
  className,
  testId,
}: {
  children: React.ReactNode;
  /** Texto para el lector de pantalla. Por defecto «Opciones». */
  label?: string;
  className?: string;
  testId?: string;
}) {
  const { t } = useTranslation();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={cn(SHEET_CLOSE_CLASS, className)}
          aria-label={label ?? t("common.options")}
          data-testid={testId}
        >
          <MoreHorizontal className="h-5 w-5" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="z-[60] min-w-[12rem]">
        {children}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Una línea del «⋯». Es el `DropdownMenuItem` de siempre, con otro nombre. */
export const SheetMoreMenuItem = DropdownMenuItem;
